import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { CopyButton, DataView, Modal } from 'components';
import { store } from 'store';
import { cutAddress } from 'utils/helpers';

import * as S from '../common.styles';

type Column = {
  id: string;
  caption: string;
  captionAlign?: 'left' | 'center' | 'right';
  getValue: (trade: any) => React.ReactNode;
  width?: number;
  align?: 'left' | 'center' | 'right';
};

export const Activity = observer(() => {
  const { trades, tradesResponse, fetchMoreTrades } = store.portfolioStore;

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    if (typeof window === 'undefined') {
      return ['market', 'leader', 'initial', 'pnl', 'roi', 'status'];
    }
    const saved = localStorage.getItem('portfolio-activity-columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return ['market', 'leader', 'initial', 'pnl', 'roi', 'status'];
      }
    }
    return ['market', 'leader', 'initial', 'pnl', 'roi', 'status'];
  });

  const isLoading = tradesResponse.isLoading;
  const isInitialLoading = isLoading && trades.length === 0;
  const visibleTrades = trades;
  const pagination = tradesResponse.data?.pagination;
  const hasMore = !!pagination && pagination.page < pagination.totalPages;

  const columns: Column[] = [
    {
      id: 'market',
      caption: 'Market',
      align: 'left',
      getValue: (trade) => (
        <div>
          <S.TokenCell>
            {trade.icon && <S.MarketIcon src={trade.icon} alt="" loading="lazy" />}
            <S.Symbol>{trade.title}</S.Symbol>
          </S.TokenCell>
          <div style={{ fontSize: '12px', color: 'rgba(120, 160, 190, 0.7)' }}>{trade.outcome}</div>
        </div>
      ),
    },
    {
      id: 'leader',
      caption: 'Leader',
      width: 120,
      align: 'left',
      getValue: (trade) => (
        <S.Address>
          {trade.leaderProfileImage && (
            <S.EntityIcon src={trade.leaderProfileImage} alt="" loading="lazy" />
          )}
          <S.AddressLink
            href={`https://polygonscan.com/address/${trade.leader}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {trade.leaderName || cutAddress(trade.leader)}
          </S.AddressLink>
          <CopyButton copyText={trade.leader} />
        </S.Address>
      ),
    },
    {
      id: 'initial',
      caption: 'Invested',
      captionAlign: 'right',
      width: 90,
      align: 'right',
      getValue: (trade) => <DataView value={trade.initialValue} type="dollars" toFixed={2} />,
    },
    {
      id: 'pnl',
      caption: 'PnL',
      captionAlign: 'right',
      width: 80,
      align: 'right',
      getValue: (trade) => (
        <DataView value={trade.totalPnl} hasSign hasColor type="dollars" toFixed={2} />
      ),
    },
    {
      id: 'roi',
      caption: 'ROI',
      captionAlign: 'right',
      width: 70,
      align: 'right',
      getValue: (trade) => (
        <DataView value={trade.roi} hasSign hasColor type="percent" toFixed={1} />
      ),
    },
    {
      id: 'status',
      caption: 'Status',
      width: 90,
      align: 'left',
      getValue: (trade) => (
        <span>
          {trade.isOpen ? 'Open' : trade.closedAt ? 'Closed' : trade.closeReason || 'Unknown'}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('portfolio-activity-columns', JSON.stringify(visibleColumns));
    }
  }, [visibleColumns]);

  useEffect(() => {
    const table = tableWrapperRef.current;
    if (!table) return;
    const handleScroll = () => {
      setIsScrolled(table.scrollTop > 0);
    };
    table.addEventListener('scroll', handleScroll);
    return () => table.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleColumn = (columnId: string) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      }
      return [...prev, columnId];
    });
  };

  const filteredColumns = columns.filter((col) => visibleColumns.includes(col.id));

  if (isInitialLoading) {
    return (
      <S.ActivitySection>
        <S.SectionHeader>
          <S.SectionHeaderLeft>
            <S.SectionDot />
            <S.SectionTitle>Closed Positions</S.SectionTitle>
          </S.SectionHeaderLeft>
        </S.SectionHeader>
        <S.EmptyState>
          <div>LOADING...</div>
          <div>Fetching closed positions</div>
        </S.EmptyState>
      </S.ActivitySection>
    );
  }

  return (
    <S.ActivitySection>
      <S.SectionHeader>
        <S.SectionHeaderLeft>
          <S.SectionDot />
          <S.SectionTitle>Closed Positions</S.SectionTitle>
        </S.SectionHeaderLeft>
        <S.ColumnsButton onClick={() => setIsColumnsModalOpen(true)}>
          <S.ColumnsIcon />
          Columns
          <S.ChevronIcon />
        </S.ColumnsButton>
      </S.SectionHeader>

      <S.DesktopOnlyTableWrapper ref={tableWrapperRef}>
        <S.Table $isScrolled={isScrolled} hidden={trades.length === 0}>
          <thead>
            <tr>
              {filteredColumns.map((c) => (
                <th
                  key={c.id}
                  style={{ width: c.width, textAlign: c.captionAlign || c.align || 'left' }}
                >
                  {c.caption}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleTrades.map((trade) => (
              <tr key={trade.id}>
                {filteredColumns.map((c) => (
                  <td key={c.id} style={{ textAlign: c.align || 'left' }}>
                    <S.Cell
                      style={{ justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}
                    >
                      {c.getValue(trade)}
                    </S.Cell>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </S.Table>

        {hasMore && (
          <S.LoadMoreButton onClick={!isLoading ? fetchMoreTrades : undefined}>
            {isLoading
              ? 'Loading...'
              : `Load more (${trades.length} / ${pagination?.total || trades.length})`}
          </S.LoadMoreButton>
        )}

        {trades.length === 0 && (
          <S.EmptyState>
            <div>// NO DATA</div>
            <div>No closed positions yet</div>
          </S.EmptyState>
        )}
      </S.DesktopOnlyTableWrapper>

      <S.MobileCards>
        {visibleTrades.map((trade) => (
          <S.MobileCard key={trade.id}>
            <S.MobileCardHeader>
              <S.MobileCardTitle>
                <S.TokenCell>
                  {trade.icon && <S.MarketIcon src={trade.icon} alt="" loading="lazy" />}
                  <S.Symbol>{trade.title}</S.Symbol>
                </S.TokenCell>
              </S.MobileCardTitle>
              <S.MobileCardSubtitle>{trade.outcome}</S.MobileCardSubtitle>
              <S.Address>
                {trade.leaderProfileImage && (
                  <S.EntityIcon src={trade.leaderProfileImage} alt="" loading="lazy" />
                )}
                <S.AddressLink
                  href={`https://polygonscan.com/address/${trade.leader}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {trade.leaderName || cutAddress(trade.leader)}
                </S.AddressLink>
                <CopyButton copyText={trade.leader} />
              </S.Address>
            </S.MobileCardHeader>

            <S.MobileStatsGrid>
              <S.MobileStat>
                <S.MobileStatLabel>Invested</S.MobileStatLabel>
                <S.MobileStatValue>
                  <DataView value={trade.initialValue} type="dollars" toFixed={2} />
                </S.MobileStatValue>
              </S.MobileStat>
              <S.MobileStat>
                <S.MobileStatLabel>PnL</S.MobileStatLabel>
                <S.MobileStatValue>
                  <DataView value={trade.totalPnl} hasSign hasColor type="dollars" toFixed={2} />
                </S.MobileStatValue>
              </S.MobileStat>
              <S.MobileStat>
                <S.MobileStatLabel>ROI</S.MobileStatLabel>
                <S.MobileStatValue>
                  <DataView value={trade.roi} hasSign hasColor type="percent" toFixed={1} />
                </S.MobileStatValue>
              </S.MobileStat>
              <S.MobileStat>
                <S.MobileStatLabel>Status</S.MobileStatLabel>
                <S.MobileStatValue>
                  {trade.isOpen ? 'Open' : trade.closedAt ? 'Closed' : trade.closeReason || 'Unknown'}
                </S.MobileStatValue>
              </S.MobileStat>
            </S.MobileStatsGrid>
          </S.MobileCard>
        ))}
      </S.MobileCards>

      <Modal isOpen={isColumnsModalOpen} onClose={() => setIsColumnsModalOpen(false)}>
        <S.ColumnsModalContent>
          <S.ModalTitle>Columns</S.ModalTitle>
          <S.ColumnsList>
            {columns.map((col) => {
              const isChecked = visibleColumns.includes(col.id);
              return (
                <S.ColumnItem key={col.id}>
                  <S.ColumnName>{col.caption}</S.ColumnName>
                  <S.ToggleWrapper>
                    <S.ToggleSwitch>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleColumn(col.id)}
                      />
                      <span />
                    </S.ToggleSwitch>
                  </S.ToggleWrapper>
                </S.ColumnItem>
              );
            })}
          </S.ColumnsList>
        </S.ColumnsModalContent>
      </Modal>
    </S.ActivitySection>
  );
});
